import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  FileText, 
  Eye,
  User,
  GraduationCap,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import { toast } from 'sonner';

export default function ViewReportCardsPage() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedTerm, setSelectedTerm] = useState('1');
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  
  const isTeacher = currentUser?.role === 'teacher';
  const isPrincipal = ['principal', 'admin', 'academic_principal', 'deputy_principal'].includes(currentUser?.role);

  useEffect(() => {
    if (!currentUser) {
      navigate('/dashboard');
      return;
    }
    
    if (isTeacher) {
      fetchTeacherStudents();
    } else if (isPrincipal) {
      fetchAllStudents();
    } else {
      navigate('/dashboard');
    }
  }, []);

  const fetchTeacherStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const classesRes = await fetch('/api/classes/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classes = await classesRes.json();
      setTeacherClasses(Array.isArray(classes) ? classes : []);
      
      // Fetch students from all teacher classes
      const allStudents = [];
      const seenIds = new Set();
      for (const cls of (Array.isArray(classes) ? classes : [])) {
        const studentsRes = await fetch(`/api/classes/${cls.id}/students`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const clsStudents = await studentsRes.json();
        if (Array.isArray(clsStudents)) {
          for (const s of clsStudents) {
            if (!seenIds.has(s.id)) {
              seenIds.add(s.id);
              allStudents.push({
                ...s,
                year_level: cls.year,
                class_name: cls.name,
                class_id: cls.id,
              });
            }
          }
        }
      }
      setStudents(allStudents);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      const data = await apiService.listStudents();
      setStudents(data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  // Get unique year levels
  const yearLevels = [...new Set(students.map(s => {
    if (s.year_level) return String(s.year_level);
    const match = s.department?.match(/Year (\d+)/);
    return match ? match[1] : null;
  }).filter(Boolean))].sort((a, b) => parseInt(a) - parseInt(b));
  
  // Filter students
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const studentYear = student.year_level ? String(student.year_level) : (student.department?.match(/Year (\d+)/)?.[1] || '');
    const matchesYear = selectedYear === 'all' || studentYear === selectedYear;
    const matchesClass = selectedClass === 'all' || student.class_id === selectedClass;
    return matchesSearch && matchesYear && matchesClass;
  });
  
  const viewReportCard = (student) => {
    const yearLevel = student.year_level || student.department?.match(/Year (\d+)/)?.[1] || '11';
    navigate(`/dashboard/student-report/${student.id}?year=${yearLevel}&term=${selectedTerm}`);
  };

  return (
    <div className="space-y-6" data-testid="view-report-cards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-blue-600" />
            View Report Cards
          </h1>
          <p className="text-gray-600 mt-1">
            {isTeacher 
              ? 'View report cards for students in your classes' 
              : 'View student report cards for your school'}
          </p>
        </div>
        <Badge className={isPrincipal ? "bg-purple-600" : "bg-blue-600"}>
          {isPrincipal ? 'Full Access' : 'My Classes'}
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by student name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="report-card-search"
                />
              </div>
            </div>
            
            {isTeacher && teacherClasses.length > 1 && (
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All My Classes</SelectItem>
                  {teacherClasses.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Year Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearLevels.map(year => (
                  <SelectItem key={year} value={year}>Year {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Term 1</SelectItem>
                <SelectItem value="2">Term 2</SelectItem>
                <SelectItem value="3">Term 3</SelectItem>
                <SelectItem value="4">Term 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Students ({filteredStudents.length})
          </CardTitle>
          <CardDescription>
            {isTeacher 
              ? 'Students from your classes — report card shows your subject grades and attendance'
              : 'Select a student to view their full report card'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>No students found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Year Level</TableHead>
                    {isTeacher && <TableHead>Class</TableHead>}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.slice(0, 100).map((student) => {
                    const yearLevel = student.year_level || student.department?.match(/Year (\d+)/)?.[1] || '?';
                    return (
                      <TableRow key={student.id} data-testid={`report-card-row-${student.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            {student.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">Year {yearLevel}</Badge>
                        </TableCell>
                        {isTeacher && (
                          <TableCell>
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <BookOpen className="h-3 w-3" />
                              {student.class_name || 'N/A'}
                            </span>
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            onClick={() => viewReportCard(student)}
                            className="gap-1"
                            data-testid={`view-report-btn-${student.id}`}
                          >
                            <Eye className="h-4 w-4" />
                            View Report
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredStudents.length > 100 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  Showing first 100 students. Use search to find specific students.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
